/**
 * Transferir propriedade da viagem.
 *
 * A operação mais grave do produto: quem transfere deixa de controlar a
 * viagem, e não há como desfazer sozinho. Por isso três barreiras, e nenhuma
 * delas é enfeite:
 *
 * 1. **Escolher a pessoa** — só participantes ativos, e nunca você.
 * 2. **Ler o que muda** — o texto diz explicitamente que a outra pessoa passa
 *    a ter controle total e que você vira administrador.
 * 3. **Digitar TRANSFERIR** — a palavra também é exigida pelo servidor, para
 *    que ela não seja apenas um obstáculo de tela.
 *
 * A atomicidade é do servidor (`transferOwnership`, com a linha da viagem
 * bloqueada). O que esta tela garante é que a sessão se atualize na hora: ao
 * concluir, as permissões efetivas são recarregadas, e quem transferiu perde
 * o que perdeu **sem precisar sair e entrar de novo**.
 */
import { ROLE_LABELS } from '@nv/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { MembroDaViagem } from '../../trip/TripContext';
import { useOnline } from '../../sync/useOnline';
import { Card, SectionLabel } from '../../ui/primitives';
import { Field } from '../auth/Field';
import { GrupoError, transferOwnership } from './api';

const PALAVRA = 'TRANSFERIR';

export function TransferirScreen({
  tripId,
  membros,
  meuMemberId,
  souDono,
}: {
  tripId: string;
  membros: MembroDaViagem[];
  meuMemberId: string;
  souDono: boolean;
}) {
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const online = useOnline();

  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [palavra, setPalavra] = useState('');

  const transferir = useMutation({
    mutationFn: (toUserId: string) => transferOwnership(tripId, toUserId),
    onSuccess: async () => {
      // Recarrega a identidade ANTES de navegar: a tela seguinte precisa
      // desenhar com o papel novo, não com o antigo.
      await cliente.invalidateQueries({ queryKey: ['viagem'] });
      await cliente.invalidateQueries({ queryKey: ['grupo', tripId] });
      await cliente.refetchQueries({ queryKey: ['viagem'] });
      navegar('/grupo', { replace: true });
    },
  });

  if (!souDono) {
    return (
      <section className="grupo">
        <header className="fin-nova__header">
          <h1 className="grupo__titulo">Transferir propriedade</h1>
        </header>
        <Card variant="chip">
          <p className="auth-note" role="status">
            Só quem é dono da viagem pode transferir a propriedade.
          </p>
        </Card>
      </section>
    );
  }

  const elegiveis = membros.filter((m) => m.memberId !== meuMemberId);
  const alvo = elegiveis.find((m) => m.memberId === escolhido);
  const pode = Boolean(alvo) && palavra.trim().toUpperCase() === PALAVRA && online;

  return (
    <section className="grupo">
      <header className="fin-nova__header">
        <h1 className="grupo__titulo">Transferir propriedade</h1>
      </header>

      {!online && (
        <Card variant="chip">
          <p className="auth-note" role="status">
            Sem conexão. A transferência só acontece online — ela muda quem
            controla a viagem e precisa ser confirmada pelo servidor.
          </p>
        </Card>
      )}

      <SectionLabel>NOVO DONO</SectionLabel>
      <Card variant="surf">
        {elegiveis.length === 0 ? (
          <p className="auth-note">
            Não há outro participante ativo para receber a propriedade.
          </p>
        ) : (
          elegiveis.map((m) => (
            <label className="fin-nova__modo" key={m.memberId}>
              <input
                type="radio"
                name="novo-dono"
                checked={escolhido === m.memberId}
                disabled={!online}
                onChange={() => setEscolhido(m.memberId)}
              />
              <span>
                <span className="fin-nova__modo-label">{m.name}</span>
                <span className="fin-nova__modo-nota">{ROLE_LABELS[m.role]}</span>
              </span>
            </label>
          ))
        )}
      </Card>

      <Card variant="chip">
        <p className="auth-note">
          <strong>Apenas uma pessoa pode ser proprietária da viagem.</strong> Ao
          transferir, {alvo?.name ?? 'a pessoa escolhida'} passa a ter controle
          total — inclusive sobre as suas permissões — e você vira
          administrador. Só quem é dono pode transferir de volta.
        </p>
      </Card>

      <Field
        label={`Digite ${PALAVRA} para confirmar`}
        value={palavra}
        onChange={(e) => setPalavra(e.target.value)}
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        disabled={!online}
      />

      {transferir.isError && (
        <p className="auth-error" role="alert">
          {(transferir.error as GrupoError).message}
        </p>
      )}

      <button
        type="button"
        className="nv-btn nv-btn--danger nv-btn--block"
        disabled={!pode || transferir.isPending}
        onClick={() => alvo && transferir.mutate(alvo.userId)}
      >
        {transferir.isPending
          ? 'Transferindo…'
          : `Transferir a viagem para ${alvo?.name ?? '…'}`}
      </button>
    </section>
  );
}
